import { useRouteError, useNavigate } from 'react-router-dom';

export function ErrorBoundary() {
  const error = useRouteError() as Error & { status?: number; statusText?: string };
  const navigate = useNavigate();

  const is404 = error.status === 404;
  const isNetworkError = error.message?.toLowerCase().includes('network') ||
                         error.message?.toLowerCase().includes('fetch');

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="max-w-2xl w-full">
        {/* Error Card */}
        <div className="glass-card p-8 md:p-12 shadow-2xl">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-red-700 shadow-lg shadow-red-500/30">
              <svg
                className="h-10 w-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                {is404 ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                )}
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1 className="font-roboto text-3xl font-bold text-gray-900 text-center mb-3">
            {is404 ? 'Page Not Found' : isNetworkError ? 'Connection Error' : 'Something Went Wrong'}
          </h1>

          {/* Description */}
          <p className="font-roboto text-base text-gray-600 text-center mb-8 max-w-md mx-auto">
            {is404
              ? "The page you're looking for doesn't exist or has been moved."
              : isNetworkError
              ? "Unable to connect to the server. Please check your internet connection and try again."
              : "An unexpected error occurred. Our team has been notified and we're working to fix it."}
          </p>

          {/* Error Details (in development) */}
          {import.meta.env.DEV && (
            <div className="mb-8 rounded-lg bg-slate-50 border border-slate-200/50 p-4 overflow-hidden">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="font-roboto text-sm font-semibold text-slate-900 mb-1">
                    Error Details (Development Mode)
                  </p>
                  <p className="font-mono text-xs text-slate-700 break-all">
                    {error.statusText || error.message || 'Unknown error'}
                  </p>
                  {error.stack && (
                    <details className="mt-2">
                      <summary className="font-roboto text-xs text-slate-600 cursor-pointer hover:text-slate-900">
                        Stack Trace
                      </summary>
                      <pre className="mt-2 font-mono text-xs text-slate-600 overflow-x-auto whitespace-pre-wrap">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200/50 bg-white px-6 py-3 font-roboto text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Go Back
            </button>

            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-primary-500 to-primary-700 px-6 py-3 font-roboto text-sm font-medium text-white shadow-lg shadow-primary-500/30 transition-all duration-200 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Go to Dashboard
            </button>
          </div>

          {/* Additional Help */}
          <div className="mt-8 pt-6 border-t border-slate-200/50">
            <p className="font-roboto text-sm text-gray-600 text-center">
              Need help?{' '}
              <a
                href="mailto:support@finharv.com"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
